import type {
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import type { Client } from './generated/client';
import { AuthApi } from './generated/sdk.gen';
import type { RefreshResponseDto } from './generated/types.gen';

export interface TokenProvider {
  /**
   * Get the current access token
   */
  getAccessToken: () => string | undefined;
  /**
   * Get the current refresh token
   */
  getRefreshToken: () => string | undefined;
  /**
   * Update tokens after refresh
   */
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  /**
   * Called when refresh fails because the refresh token is invalid/expired (HTTP 401).
   */
  onAuthInvalidated?: () => void | Promise<void>;
  /**
   * @deprecated Use onAuthInvalidated. Kept for backward compatibility.
   */
  onLogout?: () => void | Promise<void>;
  /**
   * Called when refresh fails transiently (network/5xx) — session may still be valid.
   */
  onSessionDegraded?: () => void | Promise<void>;
  /**
   * Re-read tokens from durable storage (e.g. another tab already refreshed).
   * Called before treating a refresh 401 as session death.
   */
  resyncTokens?: () => void;
}

export interface AuthConfig {
  /**
   * The client instance to configure
   */
  client: Client;
  /**
   * Token provider functions for getting/setting tokens
   */
  tokenProvider: TokenProvider;
  /**
   * Optional language header name (default: 'x-custom-lang')
   */
  languageHeaderName?: string;
  /**
   * Optional function to resolve the browser locale
   */
  resolveLocale?: () => string | undefined;
}

type AuthRequestConfig = InternalAxiosRequestConfig & {
  retried?: boolean;
  authRefreshInvalidated?: boolean;
};

type RefreshTokens = { accessToken: string; refreshToken: string };

const TRANSIENT_NETWORK_CODES = new Set([
  'ERR_NETWORK',
  'ERR_CONNECTION_REFUSED',
  'ERR_CONNECTION_TIMED_OUT',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_CANCELED',
  'ECONNREFUSED',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ENOTFOUND',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'ECANCELED',
]);

const TRANSIENT_NETWORK_MESSAGE_PATTERNS = [
  'network error',
  'connection refused',
  'timed out',
  'timeout',
  'failed to fetch',
  'canceled',
  'cancelled',
  'aborted',
];

/** Wait for another tab's persist write before treating refresh 401 as logout. */
export const REFRESH_TOKEN_RESYNC_GRACE_MS = 50;

type HttpLikeError = {
  code?: string;
  message?: string;
  response?: {
    status?: number;
  };
};

export function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  return (error as HttpLikeError).response?.status;
}

/**
 * True only when the server rejected the credential (expired/invalid token).
 * 403 is a permission failure and must not clear the session.
 */
export function isExplicitAuthFailure(status: number | undefined): boolean {
  return status === 401;
}

export function isTransientServerError(status: number | undefined): boolean {
  if (status === undefined) {
    return false;
  }
  return status === 408 || status === 429 || status >= 500;
}

export function isTransientNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const httpError = error as HttpLikeError;
  if (httpError.response) {
    return false;
  }

  const code = httpError.code?.toUpperCase();
  if (code && TRANSIENT_NETWORK_CODES.has(code)) {
    return true;
  }

  const message = httpError.message?.toLowerCase() ?? '';
  return TRANSIENT_NETWORK_MESSAGE_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
}

export function isRefreshFailureTransient(error: unknown): boolean {
  if (isTransientNetworkError(error)) {
    return true;
  }
  return isTransientServerError(getHttpStatus(error));
}

function setHeader(
  headers: AxiosRequestHeaders,
  key: string,
  value: string,
): void {
  if (typeof headers.set === 'function') {
    headers.set(key, value);
  } else if (typeof headers.append === 'function') {
    headers.append(key, value);
  } else {
    (headers as Record<string, string>)[key] = value;
  }
}

function hasHeader(headers: AxiosRequestHeaders, key: string): boolean {
  if (typeof headers.has === 'function') {
    return headers.has(key);
  }
  return Boolean((headers as Record<string, string | undefined>)[key]);
}

function isRefreshUrl(url: string, method: string): boolean {
  return (
    (url === '/api/v1/auth/refresh' ||
      url.endsWith('/api/v1/auth/refresh') ||
      url.includes('/api/v1/auth/refresh')) &&
    method === 'post'
  );
}

async function invokeAuthInvalidated(
  tokenProvider: TokenProvider,
): Promise<void> {
  const handler = tokenProvider.onAuthInvalidated ?? tokenProvider.onLogout;
  if (handler) {
    await handler();
  }
}

async function invokeSessionDegraded(
  tokenProvider: TokenProvider,
): Promise<void> {
  if (tokenProvider.onSessionDegraded) {
    await tokenProvider.onSessionDegraded();
  }
}

function parseRefreshResponse(response: unknown): RefreshTokens | undefined {
  if (response && typeof response === 'object' && 'data' in response) {
    const record = response as Record<string, unknown>;
    if ('error' in record) {
      return undefined;
    }
    const responseData = (response as AxiosResponse<RefreshResponseDto>).data;
    if (responseData?.token && responseData?.refreshToken) {
      return {
        accessToken: responseData.token,
        refreshToken: responseData.refreshToken,
      };
    }
  }
  return undefined;
}

async function runRefresh(
  client: Client,
  tokenProvider: TokenProvider,
): Promise<RefreshTokens> {
  const response = await AuthApi.refresh({ client });
  const tokens = parseRefreshResponse(response);
  if (!tokens) {
    throw new Error('Refresh failed: invalid response');
  }
  tokenProvider.setTokens(tokens);
  return tokens;
}

async function recoverRotatedRefreshToken(
  client: Client,
  tokenProvider: TokenProvider,
  initialRefreshToken: string,
): Promise<RefreshTokens | undefined> {
  const tokenChanged = (): boolean => {
    tokenProvider.resyncTokens?.();
    const currentRefreshToken = tokenProvider.getRefreshToken();
    return Boolean(
      currentRefreshToken && currentRefreshToken !== initialRefreshToken,
    );
  };

  if (tokenChanged()) {
    return runRefresh(client, tokenProvider);
  }

  await new Promise<void>((resolve) => {
    setTimeout(resolve, REFRESH_TOKEN_RESYNC_GRACE_MS);
  });

  if (tokenChanged()) {
    return runRefresh(client, tokenProvider);
  }

  return undefined;
}

async function attemptRefreshWithRetry(
  client: Client,
  tokenProvider: TokenProvider,
): Promise<RefreshTokens> {
  const initialRefreshToken = tokenProvider.getRefreshToken();
  if (!initialRefreshToken) {
    throw new Error('No refresh token');
  }

  try {
    return await runRefresh(client, tokenProvider);
  } catch (firstError) {
    if (!isExplicitAuthFailure(getHttpStatus(firstError))) {
      throw firstError;
    }

    const recovered = await recoverRotatedRefreshToken(
      client,
      tokenProvider,
      initialRefreshToken,
    );
    if (recovered) {
      return recovered;
    }

    throw firstError;
  }
}

/**
 * Sets up unified authentication interceptors for the API client.
 * This handles token injection, automatic token refresh, and logout only when
 * the refresh token is rejected with HTTP 401.
 *
 * @param config - Configuration object with client and token provider
 */
export function setupAuth(config: AuthConfig): void {
  const {
    client,
    tokenProvider,
    languageHeaderName = 'x-custom-lang',
    resolveLocale,
  } = config;
  const axiosInstance: AxiosInstance = client.instance;

  let refreshPromise: Promise<RefreshTokens> | undefined;

  const getSharedRefresh = (): Promise<RefreshTokens> => {
    if (!refreshPromise) {
      refreshPromise = attemptRefreshWithRetry(client, tokenProvider).finally(
        () => {
          refreshPromise = undefined;
        },
      );
    }
    return refreshPromise;
  };

  // Request interceptor: Inject tokens and language headers
  axiosInstance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const headers = requestConfig.headers ?? {};

      const url = requestConfig.url || '';
      const method = requestConfig.method?.toLowerCase() || '';
      const isRefreshEndpoint = isRefreshUrl(url, method);

      const token = isRefreshEndpoint
        ? tokenProvider.getRefreshToken()
        : tokenProvider.getAccessToken();

      if (token) {
        setHeader(headers, 'Authorization', `Bearer ${token}`);
      }

      if (resolveLocale && !hasHeader(headers, languageHeaderName)) {
        const locale = resolveLocale();
        if (locale) {
          setHeader(headers, languageHeaderName, locale);
        }
      }

      requestConfig.headers = headers;
      return requestConfig;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor: Handle 401 errors and refresh tokens
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AuthRequestConfig;
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const requestUrl = originalRequest.url || '';

      const isEndpoint = (endpoint: string) =>
        requestUrl === endpoint ||
        requestUrl.endsWith(endpoint) ||
        requestUrl.includes(endpoint);

      const isRefreshEndpoint = isEndpoint('/api/v1/auth/refresh');
      const isLogoutEndpoint = isEndpoint('/api/v1/auth/logout');

      if (isTransientNetworkError(error)) {
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        !originalRequest.retried &&
        !isRefreshEndpoint &&
        !isLogoutEndpoint
      ) {
        const refreshToken = tokenProvider.getRefreshToken();
        if (!refreshToken) {
          return Promise.reject(error);
        }

        originalRequest.retried = true;

        try {
          const tokens = await getSharedRefresh();

          setHeader(
            originalRequest.headers,
            'Authorization',
            `Bearer ${tokens.accessToken}`,
          );

          return await axiosInstance.request(originalRequest);
        } catch (refreshError: unknown) {
          if (isRefreshFailureTransient(refreshError)) {
            await invokeSessionDegraded(tokenProvider);
            return Promise.reject(refreshError);
          }

          if (isExplicitAuthFailure(getHttpStatus(refreshError))) {
            originalRequest.authRefreshInvalidated = true;
            await invokeAuthInvalidated(tokenProvider);
          }

          return Promise.reject(refreshError);
        }
      }

      if (
        error.response?.status === 401 &&
        originalRequest.authRefreshInvalidated &&
        !isLogoutEndpoint
      ) {
        await invokeAuthInvalidated(tokenProvider);
      }

      return Promise.reject(error);
    },
  );
}
